import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type {
  AuctionsOfAuctionTileParams,
  AuctionsOfAuctionTileResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';
import { isVerificationProblemWithoutField } from 'src/utils/is-verification-problem-without-field';

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  tileId: Uuid;
  params: AuctionsOfAuctionTileParams;
};

type ExecuteOptions = {
  url: string;
  params: AuctionsOfAuctionTileParams;
};

export function useAuctionsOfAuctionTile({
  root,
  tileId,
  params,
}: HookOptions) {
  const auctions = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<AuctionsOfAuctionTileResponse>({
        ...baseConfig,
        url,
        params,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 404)) {
          // either unknown or too old
          return error;
        }
        if (isVerificationProblemWithoutField(error)) {
          // e.g. no relevant auctions found in this auction tile
          return error;
        }
        throw error;
      },
    }
  );

  const auctionsLink = root?.response?.data._links.auctionsOfAuctionTile;

  useEffect(() => {
    if (!auctionsLink) return;

    const url = urlTemplate.parse(auctionsLink.href).expand({ tileId });
    auctions.execute({ url, params });
    // oxlint-disable-next-line exhaustive-deps
  }, [auctionsLink, tileId, params]);

  if (!auctionsLink) throw new NotFound();

  return auctions;
}

export type AuctionsOfAuctionTileRequest = ReturnType<
  typeof useAuctionsOfAuctionTile
>;
